import React, { useState } from "react";
import "../../styles/NavigatorСodeBoardType2.css";
import { GameStyleType } from "../types/types";
import { useGame } from "../../GameContext";
import { useTimerActions } from "../../TimerContext";
import MESSAGES from "../hooks/message";
import RenderBoardLogo from "../render/RenderBoardLogo";
import RenderGameCard from "../render/RenderGameCard";
import RenderCode from "./RenderCode";
import findWordPaths from "../hooks/FindWordPath";
import shuffleArray from "../hooks/shuffleArray";
import createWordNumbers from "./createWordNumbers";
import RenderWordSearch from "./RenderWordSearch";
const Board: React.FC<GameStyleType> = ({ rounds, random }) => {
  const { setMessage, setGuessesWords, setGameStarted, isGameStarted } =
    useGame();

  const { startTimer, stopTimer, resetTimer } = useTimerActions();
  //
  const createWordOrder = () => {
    const indexes = rounds.map((_, index) => index);
    return random ? shuffleArray(indexes) : indexes;
  };
  const normalizeWord = (text: string) =>
    text.replace(/[|\s]/g, "").toUpperCase();
  //
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());

  const [isGameFinished, setIsGameFinished] = useState<boolean>(false);
  const [blockUserInput, setBlockUserInput] = useState<boolean>(true);

  const [answerValue, setAnswerValue] = useState<string>("");
  const [answerStatus, setAnswerStatus] = useState<
    "idle" | "correct" | "wrong"
  >("idle");

  const [wordIndex, setWordIndex] = useState(0); // номер отгадываемого слова слова
  const [roundIndex, setRoundIndex] = useState(0);

  const [order, setOrder] = useState(createWordOrder);
  const round = rounds[order[roundIndex]];

  const words = round.words.toUpperCase().split(/\s+/).filter(Boolean); // набор слов на раунд
  const isLastWord = wordIndex === words.length - 1;
  const isLastRound = roundIndex === rounds.length - 1;

  const currentWord = words[wordIndex];

  const table = round.table;

  const paths = findWordPaths(table, words, 2);
  const currentPath = paths[wordIndex] ?? null;
  const wordNumbers = createWordNumbers(table, paths);
  // ---------------

  const startRound = () => {
    setAnswerValue("");
    setAnswerStatus("idle");
    setWordIndex(0);
    setSelectedCells(new Set());
    setBlockUserInput(false);
    setGuessesWords([]);
    setMessage(MESSAGES.START);
    startTimer();
  }; //начало раунда
  const finishRound = () => {
    stopTimer();
    setBlockUserInput(true);
  }; //конец раунда

  const restartGame = () => {
    resetTimer();

    setGuessesWords([]);
    setRoundIndex(0);
    setWordIndex(0);
    setIsGameFinished(false);

    startRound();
  }; // сброс игры при переходах и окончании раунда

  const handleStart = () => {
    setGameStarted(true);
    restartGame();
  }; //начать игру

  const nextGuessed = () => {
    if (isGameFinished) {
      setOrder(createWordOrder());
      restartGame();
      return;
    }

    setRoundIndex((prev) => prev + 1);
    startRound();
  }; // далее / играть ещё

  const nextWord = () => {
    if (wordIndex >= words.length - 1) {
      return;
    }
    setSelectedCells(new Set());
    setWordIndex((prev) => prev + 1);
    setAnswerValue("");
    setAnswerStatus("idle");
  };
  const checkAnswer = (answerValue: string) => {
    if (isGameFinished || !isGameStarted || blockUserInput) return;
    const answer = normalizeWord(answerValue);
    //если пустой ответ
    if (!answer) {
      setMessage(MESSAGES.ENTER_ANSWER);
      setAnswerStatus("wrong");
      return;
    }
    //если ответ неверный
    if (answer !== normalizeWord(currentWord)) {
      setMessage(MESSAGES.ANSWER_WRONG);
      setAnswerStatus("wrong");
      return;
    }
    //если прошел проверки = ответ верный
    setAnswerStatus("correct");
    setGuessesWords((prev) => [...prev, normalizeWord(currentWord)]);

    if (isLastWord) {
      finishRound();

      if (isLastRound) {
        setIsGameFinished(true);
        setMessage(MESSAGES.GAME_FINISHED);
      } else {
        setMessage(MESSAGES.ROUND_FINISHED);
      }

      return;
    }

    // если это не последнее слово
    nextWord();

    setMessage(`Найди слово под номером: ${wordIndex + 2}`);
  };
  const toggleCell = (row: number, col: number) => {
    if (blockUserInput) return;

    const key = `${row}-${col}`;

    setSelectedCells((prev) => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnswerValue(e.target.value);

    if (answerStatus === "wrong") {
      setAnswerStatus("idle");
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || e.repeat) return;

    e.preventDefault();

    checkAnswer(answerValue);

    e.currentTarget.blur();
  };
  return (
    <div className="navigatorСode-Board">
      {/* логотип с кнопками и таймером */}
      <RenderBoardLogo
        isGameStarted={isGameStarted}
        isGameFinished={isGameFinished}
        onNext={nextGuessed}
        onStart={handleStart}
      />

      <div className="navigatorСode-board-game">
        <RenderCode
          isGameFinished={isGameFinished}
          isGameStarted={isGameStarted}
          path={currentPath}
          tableRows={table.length}
          tableColumns={table[0].length}
          wordIndex={wordIndex}
        />
        {/* два игровых поля*/}
        <div className="navigatorCode-board-game-content">
          <RenderGameCard isGameFinished={isGameFinished}>
            <RenderWordSearch
              table={table}
              selectedCells={selectedCells}
              wordNumbers={wordNumbers}
              toggleCell={toggleCell}
            />
          </RenderGameCard>
        </div>
        {/* поле ввода с кнопкой */}
        <div className="navigatorСode-button-input-board">
          <div className="navigatorСode-subtitle">Введи ответ: </div>
          <input
            className={"navigatorСode-input navigatorСode-" + answerStatus}
            disabled={blockUserInput}
            value={answerValue}
            onFocus={() => setAnswerStatus("idle")}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
          ></input>
          {!blockUserInput && (
            <button
              title="Проверить ответ"
              className="navigatorСode-buttonCheck"
              onClick={() => checkAnswer(answerValue)}
            >
              Проверить
            </button>
          )}
          {(blockUserInput && isGameStarted && !isGameFinished) && (
            <button className="navigatorСode-buttonCheck" onClick={nextGuessed}>
              Далее
              <img
                alt=""
                className="navigatorСode-logoRun"
                src="/img/button/run-fix.svg"
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Board;
